import { OauthService } from '@monsido/oauth/oauth.service';
import { Injectable } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { IssueTypeEnum } from '@monsido/core/enum/issue-type.enum';
import { ComponentType } from '@angular/cdk/overlay';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { DataPrivacyComponent } from '@monsido/modules/data-privacy/components/data-privacy-issue/data-privacy-issue.component';
import { MonUIRouterTransitionService } from '../ui-router-transition-service/ui-router-transition-service';
import {
    AccessibilityIssueComponent,
} from '@client/app/modules/accessibility/components/accessibility-issue/accessibility-issue.component';
import { QaBrokenIssueComponent } from '@client/app/modules/qa/components/qa-broken-issue/qa-broken-issue.component';
import { safeClone } from '@client/app/shared/utils/object-utils';
import {
    QaMisspellingIssueComponent,
} from '@client/app/modules/qa/components/qa-misspelling-issue/qa-misspelling-issue.component';

@Injectable()
export class OverlayService {

    constructor (
        private eventService: MonEventService,
        private stateService: StateService,
        private uiRouterGlobals: UIRouterGlobals,
        private monUiRouterTransitionService: MonUIRouterTransitionService,
        private oauthService: OauthService,
    ) {}

    async openOverlay (params: Record<string, string>): Promise<void> {
        if (!this.oauthService.isAuthenticated()) {
            return Promise.resolve();
        }

        const { issueOverlayIssueId, issueOverlayIssueType } = params;
        let component: ComponentType<unknown> | undefined;

        switch (issueOverlayIssueType) {
            case IssueTypeEnum.accessibility:
                component = AccessibilityIssueComponent;
                break;
            case IssueTypeEnum.qaBrokenLink:
            case IssueTypeEnum.qaBrokenImage:
                component = QaBrokenIssueComponent;
                break;
            case IssueTypeEnum.misspellings:
            case IssueTypeEnum.potentialMisspellings:
                component = QaMisspellingIssueComponent;
                break;
            case IssueTypeEnum.dataPrivacy:
                component = DataPrivacyComponent;
                break;
        }

        if (issueOverlayIssueId && component) {
            if (this.hasNoActiveTransition()) {
                this.requestOverlay(issueOverlayIssueId, component);
            } else {
                await this.whenCurrentTransitionEnds();
                this.requestOverlay(issueOverlayIssueId, component);
            }
        }
    }

    closeAllDialogs (): void {
        this.eventService.run(MON_EVENTS.CLOSE_ALL_OVERLAY_DIALOGS);
    }

    private requestOverlay (issueId: string, component: ComponentType<unknown>): void {
        const beforeCloseCallbacks: Array<() => Promise<boolean>> = [];
        const beforeCloseCb = async (): Promise<boolean> => {
            for (const callback of beforeCloseCallbacks) {
                if (!(await callback())) {
                    return false;
                }
            }
            await this.removeOverlayQueryParams();
            return true;
        };

        this.eventService.run(MON_EVENTS.LOAD_NG2_DIALOG, {
            component,
            data: {
                issueId,
                beforeCloseCallbacks,
            },
            beforeCloseCb,
        });
    }

    private async removeOverlayQueryParams (): Promise<void> {
        if (this.hasNoActiveTransition()) {
            await this.doCloseTransition();
        } else {
            await this.whenCurrentTransitionEnds();
            await this.doCloseTransition();
        }
    }

    private hasNoActiveTransition (): boolean {
        let noActiveTransition = true;

        try {
            noActiveTransition = !this.uiRouterGlobals.transition.isActive();
        } catch (_e) {
            // uiRouter silently fails, if there is no active transition
        }

        return noActiveTransition;
    }

    private async doCloseTransition (): Promise<void> {
        await this.monUiRouterTransitionService.onTransitionFinishedAsync();
        const newParams = safeClone(this.uiRouterGlobals.params);
        delete newParams.issueOverlayIssueId;
        delete newParams.issueOverlayContentId;
        delete newParams.issueOverlayIssueType;
        delete newParams.issueOverlayPageId;
        delete newParams.issueOverlayPageLinkId;
        delete newParams.issueOverlayCheckId;
        delete newParams.issueOverlaySourceCodeId;
        delete newParams.issueOverlay;
        delete newParams.time;

        await this.stateService.transitionTo(
            this.uiRouterGlobals.$current.name,
            // TODO: add a handler for multiple overlays here
            newParams,
        );
    }

    private whenCurrentTransitionEnds (): Promise<void> {
        return new Promise((resolve) => {
            this.monUiRouterTransitionService.onTransitionFinished(() => {
                resolve();
            });
        });
    }
}
